import { useCallback, useEffect, useRef, useState } from 'react'

import { Form } from 'antd'
import { RcFile } from 'antd/es/upload'

import { useOrgUuid } from 'src/hooks/use-org-uuid'

import {
  CreateFileMutationVariables,
  GetFileUploadCredentialsMutationVariables,
  ThumbnailFileFragment,
} from '../../types/graphql'
import { FormFieldWrapperProps } from '../components/form/Wrapper'
import { MaxFileSize } from '../constants'
import { BreakpointComponents } from '../constants/breakpoints'
import { PathPrefixes } from '../constants/file'
import { useCreateFileMutation, useGetFileUploadCredentialsMutation } from '../fetch/files'
import { useIsCitizen } from '../layouts/AppStateContextLayout/utils'
import {
  FileUploadErrors,
  getIsEncryptedPdf,
  getIsFileExtensionAccepted,
  isImage,
  IUploader,
  uploadFile,
} from '../utils/file'

import { useCurrentBreakpoint } from './use-current-breakpoint'

export enum FileUploadStatuses {
  Default = 'default',
  Uploading = 'uploading',
  Done = 'done',
  Error = 'error',
  Existing = 'existing',
}

export interface FileUploadState {
  name: string
  setName: (value: string) => void
  url: string | undefined
  thumbnailUrl: string | undefined
  path: string
  status: FileUploadStatuses
  progress: number
  govWellFileId: number | undefined
  size: number | undefined
  type: string | undefined
  removeFile: () => void
  beforeUpload: (file: RcFile) => Promise<boolean>
  uploadFileUrl: (fileUrl: string) => Promise<ThumbnailFileFragment | undefined>
  reset: () => void
  createdFileId: number | undefined
  errorType: string | undefined
}

export interface UseFileUploadParams {
  inputBucket?: string
  pathPrefix?: PathPrefixes
  afterUpload?: (newFile: ThumbnailFileFragment, reset: () => void) => Promise<void>
  afterRemove?: () => Promise<void>
  fieldName?: FormFieldWrapperProps['fieldName']
  accept?: string
  shouldAnnotateImageWithExifContext?: boolean
  shouldUploadFileToOpenAI?: boolean
}

export const useFileUploadState = (params: UseFileUploadParams): FileUploadState => {
  const {
    inputBucket,
    pathPrefix,
    afterUpload,
    fieldName,
    afterRemove,
    accept,
    shouldAnnotateImageWithExifContext,
    shouldUploadFileToOpenAI,
  } = params

  const { isSmall } = useCurrentBreakpoint()
  const isSmallScreen = isSmall(BreakpointComponents.FileUpload)

  const form = Form.useFormInstance()
  Form.useWatch(fieldName, form)
  const fileFragment: ThumbnailFileFragment | undefined = form?.getFieldValue(fieldName)

  const orgUuid = useOrgUuid()
  const bucket = inputBucket || orgUuid

  const [path, setPath] = useState<string>(fileFragment?.path ?? '')
  const [status, setFileUploadStatus] = useState<FileUploadStatuses>(
    fileFragment ? FileUploadStatuses.Existing : FileUploadStatuses.Default
  )
  const statusRef = useRef<FileUploadStatuses>(status)
  const handleStatusChange = useCallback((newStatus: FileUploadStatuses) => {
    statusRef.current = newStatus
    setFileUploadStatus(newStatus)
  }, [])

  const [progress, setFileUploadProgress] = useState<number>(0)
  const [createdFileId, setCreatedFileId] = useState<number | undefined>(undefined)
  const [govWellFileId, setFileGovWellFileId] = useState<number | undefined>(fileFragment?.id)
  const [name, setFileName] = useState(fileFragment?.name ?? '')
  const [size, setFileSize] = useState<number | undefined>(fileFragment?.size ?? undefined)
  const [type, setFileType] = useState<string | undefined>(fileFragment?.type ?? undefined)
  const [url, setUrl] = useState<string | undefined>(fileFragment?.url?.signedUrl ?? undefined)
  const [thumbnailUrl, setThumbnailUrl] = useState<string | undefined>(
    fileFragment?.thumbnailUrl?.signedUrl ?? undefined
  )
  const [errorType, setErrorType] = useState<string | undefined>(undefined)

  const { mutateAsync: createFileRaw } = useCreateFileMutation()
  const { mutateAsync: getFileUploadCredentialsRaw } = useGetFileUploadCredentialsMutation()

  const createFile = useCallback(
    async (input: CreateFileMutationVariables) => {
      const res = await createFileRaw(input)
      return res.createFile
    },
    [createFileRaw]
  )
  const getFileUploadCredentials = useCallback(
    async (input: GetFileUploadCredentialsMutationVariables) => {
      const res = await getFileUploadCredentialsRaw(input)
      return res.getFileUploadCredentials
    },
    [getFileUploadCredentialsRaw]
  )

  const removeFile = () => {
    if (thumbnailUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(thumbnailUrl)
    }
    if (url?.startsWith('blob:') && url !== thumbnailUrl) {
      URL.revokeObjectURL(url)
    }
    if (uploaderRef.current) {
      uploaderRef.current.abort()
    }
    handleStatusChange(FileUploadStatuses.Default)
    if (form && fieldName) {
      form.setFieldValue(fieldName, '')
      void form.validateFields([fieldName])
    }
    reset()
    void afterRemove?.()
  }

  const uploaderRef = useRef<IUploader>()
  const commonUploadFileProps = {
    bucket,
    createFile,
    getFileUploadCredentials,
    isSmallScreen,
    onError: setErrorType,
    pathPrefix,
    setFileName,
    setFileSize,
    setFileUploadProgress,
    setFileUploadStatus: handleStatusChange,
    setPath,
    setUploaderRef: (uploader: IUploader) => {
      uploaderRef.current = uploader
    },
    shouldAnnotateImageWithExifContext,
    shouldUploadFileToOpenAI,
  }

  const afterUploadInternal = async (fileId: number, newFile: RcFile | ThumbnailFileFragment) => {
    setCreatedFileId(fileId)
    if (form && fileId && fieldName) {
      form.setFieldValue(fieldName, newFile)
      try {
        await form.validateFields([fieldName])
      } catch {
        // Do nothing
      }
    }
  }

  const isCitizen = useIsCitizen()

  const beforeUpload = async (newFile: RcFile) => {
    if (!getIsFileExtensionAccepted({ accept, fileName: newFile.name })) {
      setFileName(newFile.name)
      setErrorType(FileUploadErrors.UnsupportedFileType)
      handleStatusChange(FileUploadStatuses.Error)
      return false
    }

    if (isCitizen && newFile.size > MaxFileSize) {
      setErrorType(FileUploadErrors.TooBig)
      handleStatusChange(FileUploadStatuses.Error)
      return false
    }

    if (await getIsEncryptedPdf(newFile)) {
      setErrorType(FileUploadErrors.EncryptedPdf)
      handleStatusChange(FileUploadStatuses.Error)
      return false
    }

    setFileType(newFile.type)
    if (isImage(newFile.type)) {
      const localPreviewUrl = URL.createObjectURL(newFile)
      setUrl(localPreviewUrl)
      setThumbnailUrl(localPreviewUrl)
    }

    const uploadResult = await uploadFile({
      ...commonUploadFileProps,
      file: newFile,
    })
    if (!uploadResult) {
      return false
    }
    await afterUpload?.(uploadResult, reset)
    await afterUploadInternal(uploadResult.id, uploadResult)
    return false
  }

  const uploadFileUrl = async (fileUrl: string) => {
    const newFile = await uploadFile({
      ...commonUploadFileProps,
      fileUrl,
    })
    if (newFile) {
      await afterUpload?.(newFile, reset)
      await afterUploadInternal(newFile.id, newFile)
      return newFile
    }
    return undefined
  }

  const reset = useCallback(() => {
    setThumbnailUrl((prev) => {
      if (prev?.startsWith('blob:')) {
        URL.revokeObjectURL(prev)
      }
      return undefined
    })
    setUrl((prev) => {
      if (prev?.startsWith('blob:')) {
        URL.revokeObjectURL(prev)
      }
      return undefined
    })
    setPath('')
    setFileName('')
    setFileSize(undefined)
    setFileType(undefined)
    handleStatusChange(FileUploadStatuses.Default)
    setFileUploadProgress(0)
  }, [handleStatusChange])

  useEffect(() => {
    // Sync form state with regular state in case the file field value is modified via form state, e.g. via form auto-fill
    if (fileFragment?.path && fileFragment.path !== path) {
      reset()
      setPath(fileFragment?.path)
      setFileName(fileFragment?.name ?? '')
      setUrl(fileFragment?.url?.signedUrl)
      setThumbnailUrl(fileFragment?.thumbnailUrl?.signedUrl)
      setFileGovWellFileId(fileFragment?.id)
      setFileSize(fileFragment?.size ?? undefined)
      setFileType(fileFragment?.type)
      handleStatusChange(FileUploadStatuses.Existing)
      setFileUploadProgress(0)
    }
  }, [fileFragment, handleStatusChange, path, reset])

  const handleNameChanged = useCallback(
    (newName: string) => {
      setFileName(newName)
      const file = form.getFieldValue(fieldName)
      if (file) {
        // Update the name in the form field value, otherwise it won't reflect in the application form review step
        form.setFieldValue(fieldName, {
          ...file,
          name: newName,
        })
      }
    },
    [fieldName, form]
  )

  return {
    name,
    setName: handleNameChanged,
    url,
    thumbnailUrl,
    path,
    status,
    size,
    type,
    progress,
    govWellFileId,
    removeFile,
    beforeUpload,
    uploadFileUrl,
    createdFileId,
    reset,
    errorType,
  }
}
